import { type Balance, formatCurrency, LOGIN_METHOD } from '@gofranz/common';
import {
  OidcSyncFromIdpButton,
  OidcTwoFactorRedirect,
  useIdpHandoff,
  useLanguageAwareRouting,
  useOidcRefreshOnReturn,
} from '@gofranz/common-components';
import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Notification,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandWindows,
  IconCheck,
  IconCopy,
  IconHistory,
  IconKey,
  IconMail,
  IconPlus,
  IconShieldHalf,
  IconWallet,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NewDepositForm } from '../../components/Billing/Deposit';
import { TwoFactorSettings } from '../../components/Security/TwoFactorSettings';
import { API_BASE_URL } from '../../constants';
import { useRustyState } from '../../state';

export interface AccountProfilePageProps {
  serviceDomain: string;
  serviceEmail: string;
}

const PROVIDERS_WITH_PUBLIC_KEY: ReadonlyArray<LOGIN_METHOD> = [
  LOGIN_METHOD.NOSTR,
  LOGIN_METHOD.MANUAL,
  LOGIN_METHOD.PRIVATE_KEY,
];

function providerIcon(method?: LOGIN_METHOD) {
  switch (method) {
    case LOGIN_METHOD.GOOGLE:
      return <IconBrandGoogle size={20} />;
    case LOGIN_METHOD.GITHUB:
      return <IconBrandGithub size={20} />;
    case LOGIN_METHOD.MICROSOFT:
      return <IconBrandWindows size={20} />;
    case LOGIN_METHOD.EMAIL_MAGIC_LINK:
      return <IconMail size={20} />;
    default:
      return <IconKey size={20} />;
  }
}

export function AccountProfilePage({ serviceEmail }: AccountProfilePageProps) {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const { api } = useRustyState.getState();
  const session = useRustyState((state) => state.api?.auth?.getSession());
  const loginMethod = session?.method as LOGIN_METHOD | undefined;
  const publicKey = session?.publicKey;
  const [balance, setBalance] = useState<Balance[]>([]);
  const [addMoney, setAddMoney] = useState(false);
  const [depositStatus, setDepositStatus] = useState<'success' | 'cancel' | ''>('');
  const [syncing, setSyncing] = useState(false);

  // OIDC handoff config drives the IdP deep-links. `null` when this isn't an
  // OIDC-backed deployment (most users see the existing local UI unchanged).
  const apiRoot = API_BASE_URL.replace(/\/v1\/?$/, '');
  const isOidc = loginMethod === LOGIN_METHOD.OIDC;
  const { config: handoffConfig } = useIdpHandoff(apiRoot);

  // Auto-trigger a silent OIDC refresh when the user comes back via the
  // portal handoff banner (URL carries `?refreshed=1`). Strips the flag and
  // bounces through Hydra to repopulate the local profile from the IdP.
  useOidcRefreshOnReturn({ session, auth: api.auth });

  const handleOidcSync = async () => {
    if (!api.auth) return;
    setSyncing(true);
    try {
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}?refreshed=1`;
      const authUrl = await api.auth.initiateOidcRefresh(returnTo);
      window.location.href = authUrl;
    } catch (e) {
      console.error('OIDC sync failed:', e);
      setSyncing(false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split('?')[1]);
    const deposit = hashParams.get('deposit');

    if (deposit && (deposit === 'success' || deposit === 'cancel')) {
      setDepositStatus(deposit);
    }

    api
      .getAccountBalance()
      .then(setBalance)
      .catch(() => setBalance([]));
  }, [api]);

  const showPublicKey =
    !!loginMethod && PROVIDERS_WITH_PUBLIC_KEY.includes(loginMethod) && !!publicKey;
  const providerSubtitleKey = loginMethod ? `profile.providerSubtitle.${loginMethod}` : '';

  return (
    <>
      {depositStatus === 'success' && (
        <Notification
          color="green"
          title={t('profile.depositSuccess')}
          mb="md"
          onClose={() => setDepositStatus('')}
        >
          {t('profile.depositSuccessMessage')}
        </Notification>
      )}

      <Title order={2} mb="lg">
        {t('glob_navigation.profile')}
      </Title>

      {isOidc && (
        <Group mb="md">
          <OidcSyncFromIdpButton onSync={handleOidcSync} busy={syncing} />
        </Group>
      )}

      <Stack gap="md">
        <Card withBorder shadow="xs" radius="md" p="lg">
          <Group justify="space-between" align="flex-start" mb="md">
            <Box>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>
                {t('profile.availableFunds')}
              </Text>
              {balance && balance.length > 0 ? (
                balance.map((bal, i) => (
                  <Text key={`balance-${i}`} size="xl" fw={700}>
                    {formatCurrency(bal)}
                  </Text>
                ))
              ) : (
                <Text size="sm" c="dimmed">
                  {t('profile.noBalanceFound')}
                </Text>
              )}
            </Box>
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--mantine-color-yellow-light)',
                color: 'var(--mantine-color-yellow-8)',
              }}
            >
              <IconWallet size={20} />
            </Box>
          </Group>
          {addMoney ? (
            <>
              <NewDepositForm onCancelCb={() => setAddMoney(false)} />
              <Text size="sm" mt="xs">
                {t('profile.wirePaymentInfo')}{' '}
                <Anchor href={`mailto:${serviceEmail}`}>{serviceEmail}</Anchor>
              </Text>
            </>
          ) : (
            <>
              <Text size="sm" c="dimmed" mb="md">
                {t('profile.lowBalance')}
              </Text>
              <Button
                fullWidth
                onClick={() => setAddMoney(true)}
                leftSection={<IconPlus size={16} />}
              >
                {t('profile.addMoney')}
              </Button>
            </>
          )}
        </Card>

        <Card withBorder shadow="xs" radius="md" p="lg">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="md">
            {t('profile.authProvider')}
          </Text>
          <Paper withBorder p="md" radius="md" mb={showPublicKey ? 'md' : 0}>
            <Group gap="md">
              <Box
                style={{
                  width: 40,
                  height: 40,
                  background: 'var(--mantine-color-dark-7)',
                  color: 'var(--mantine-color-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--mantine-radius-md)',
                }}
              >
                {providerIcon(loginMethod)}
              </Box>
              <Box style={{ minWidth: 0 }}>
                <Text fw={700} size="sm">
                  {loginMethod ?? '—'}
                </Text>
                {loginMethod && (
                  <Text size="xs" c="dimmed">
                    {t(providerSubtitleKey)}
                  </Text>
                )}
              </Box>
            </Group>
          </Paper>
          {showPublicKey && (
            <>
              <Text size="xs" c="dimmed" mb="xs">
                {t('profile.publicKeyHex')}
              </Text>
              <Group gap="xs" wrap="nowrap" align="center">
                <Code
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
                  }}
                >
                  {publicKey}
                </Code>
                <CopyButton value={publicKey ?? ''}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? t('common.copied') : t('common.copy')}>
                      <ActionIcon variant="subtle" onClick={copy} aria-label={t('common.copy')}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </>
          )}
        </Card>

        <Card withBorder shadow="xs" radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Title order={4}>{t('profile.security')}</Title>
            <IconShieldHalf size={20} color="var(--mantine-color-dimmed)" />
          </Group>
          <Stack gap="md">
            {isOidc ? (
              <OidcTwoFactorRedirect
                config={handoffConfig}
                returnTo={`${window.location.origin}${window.location.pathname}${window.location.hash.split('?')[0]}?refreshed=1`}
              />
            ) : (
              <TwoFactorSettings compact />
            )}
            <Group
              justify="space-between"
              p="md"
              style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-md)',
              }}
            >
              <Group gap="md">
                <Box
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--mantine-color-default-hover)',
                    color: 'var(--mantine-color-dimmed)',
                  }}
                >
                  <IconHistory size={20} />
                </Box>
                <Box>
                  <Text fw={500} size="sm">
                    {t('profile.sessionHistory')}
                  </Text>
                </Box>
              </Group>
              <Button
                component={Link}
                to={createLanguageURL('/account/security-log')}
                variant="subtle"
                size="sm"
              >
                {t('profile.viewLog')}
              </Button>
            </Group>
          </Stack>
        </Card>

        <Card withBorder shadow="xs" radius="md" p="lg">
          <Title order={4} mb="xs">
            {t('profile.dataPrivacy')}
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            {t('profile.dataPrivacyDescription')}
          </Text>
          <Button
            component={Link}
            to={createLanguageURL('/account/data-privacy')}
            variant="default"
            fullWidth
          >
            {t('profile.manageMyData')}
          </Button>
        </Card>
      </Stack>
    </>
  );
}
